import type { HealthPromotionParseResult, HealthPromotionRecord } from '../healthPromotionTypes';
import { loadXlsx } from './parserUtils';

export const parseCurrencyValue = (value: unknown): number => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0;
    }
    if (typeof value !== 'string') return 0;

    const cleaned = value.replace(/\s+/g, '');
    if (!cleaned) return 0;

    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');
    const separatorIndex = Math.max(lastComma, lastDot);

    if (separatorIndex === -1) {
        const digitsOnly = cleaned.replace(/[^\d-]/g, '');
        const parsed = Number.parseFloat(digitsOnly);
        return Number.isFinite(parsed) ? parsed : 0;
    }

    const integerPart = cleaned.slice(0, separatorIndex).replace(/[^\d-]/g, '');
    const decimalPart = cleaned.slice(separatorIndex + 1).replace(/[^\d]/g, '');
    const composed = `${integerPart || '0'}.${decimalPart || '0'}`;
    const parsed = Number.parseFloat(composed);

    return Number.isFinite(parsed) ? parsed : 0;
};

type HeaderIndexes = {
    referenceMonth: number;
    employeeName: number;
    employeeEmail: number;
    subscriberType: number;
    planName: number;
    postDiscountPrice: number;
    status: number;
};

const isHeaderMatch = (cell: unknown, target: string): boolean => {
    if (typeof cell !== 'string') return false;
    return cell.toLowerCase().includes(target);
};

const normalizeString = (value: unknown): string => {
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number') return String(value).trim();
    return '';
};

const normalizeEmail = (value: unknown): string => {
    const email = normalizeString(value);
    return email ? email.toLowerCase() : '';
};

const resolveIndexes = (headerRow: (string | number | null)[]): HeaderIndexes => {
    const referenceMonth = headerRow.findIndex((cell) => isHeaderMatch(cell, 'reference month'));
    const employeeName = headerRow.findIndex((cell) => isHeaderMatch(cell, 'employee name'));
    const employeeEmail = headerRow.findIndex((cell) => isHeaderMatch(cell, 'employee email'));
    const subscriberType = headerRow.findIndex((cell) => isHeaderMatch(cell, 'subscriber type'));
    const planName = headerRow.findIndex((cell) => isHeaderMatch(cell, 'plan name'));
    const postDiscountPrice = headerRow.findIndex((cell) => isHeaderMatch(cell, 'post-discount'));
    const status = headerRow.findIndex((cell) => isHeaderMatch(cell, 'status'));

    if ([referenceMonth, employeeName, employeeEmail, subscriberType, planName, postDiscountPrice, status].some((index) => index === -1)) {
        throw new Error('Colunas esperadas do relatório Wellhub não foram encontradas.');
    }

    return {
        referenceMonth,
        employeeName,
        employeeEmail,
        subscriberType,
        planName,
        postDiscountPrice,
        status,
    };
};

const isCommentRow = (row: (string | number | null)[] | null | undefined): boolean => {
    if (!row || !row.length) return true;
    const firstCell = row[0];
    if (typeof firstCell !== 'string') return false;
    return firstCell.trim().startsWith('#');
};

const parseRows = (
    rows: (string | number | null)[][],
    headerIndex: number,
    indexes: HeaderIndexes,
): HealthPromotionRecord[] => {
    const records: HealthPromotionRecord[] = [];

    for (let i = headerIndex + 1; i < rows.length; i += 1) {
        const row = rows[i];
        if (isCommentRow(row)) continue;
        const statusRaw = normalizeString(row[indexes.status]) || '';
        const subscriberTypeRaw = normalizeString(row[indexes.subscriberType]) || 'Employee';
        // Filter active employees
        if (statusRaw.toLowerCase() !== 'active' || subscriberTypeRaw.toLowerCase() !== 'employee') continue;

        const email = normalizeEmail(row[indexes.employeeEmail]);
        if (!email) continue;

        const name = normalizeString(row[indexes.employeeName]) || email;
        const planName = normalizeString(row[indexes.planName]) || 'Plano';
        const referenceMonth = normalizeString(row[indexes.referenceMonth]) || null;
        const amount = parseCurrencyValue(row[indexes.postDiscountPrice]);

        records.push({
            code: email,
            name,
            email,
            subscriberType: subscriberTypeRaw,
            planName,
            amount,
            referenceMonth,
            status: statusRaw,
        });
    }

    return records;
};

export async function parseWellhubFile(file: File): Promise<HealthPromotionParseResult> {
    const xlsx = await loadXlsx();
    const fileName = file.name?.toLowerCase() ?? '';
    let workbook;
    if (fileName.endsWith('.csv')) {
        const textContent = await file.text();
        workbook = xlsx.read(textContent, { type: 'string' });
    } else {
        const buffer = await file.arrayBuffer();
        workbook = xlsx.read(buffer, { type: 'array' });
    }
    if (!workbook.SheetNames.length) {
        throw new Error('Arquivo sem abas válidas.');
    }

    let rows: (string | number | null)[][] | null = null;
    let headerIndex = -1;

    for (const sheetName of workbook.SheetNames) {
        const sheet = workbook.Sheets[sheetName];
        if (!sheet) continue;

        const sheetRows = xlsx.utils.sheet_to_json(sheet, {
            header: 1,
            raw: true,
            defval: null,
        }) as (string | number | null)[][];

        const candidateHeaderIndex = sheetRows.findIndex(
            (row) =>
                Array.isArray(row) &&
                row.length >= 6 &&
                !isCommentRow(row) &&
                row.some((cell) => isHeaderMatch(cell, 'reference month')) &&
                row.some((cell) => isHeaderMatch(cell, 'subscriber type')),
        );

        if (candidateHeaderIndex !== -1) {
            rows = sheetRows;
            headerIndex = candidateHeaderIndex;
            break;
        }
    }

    if (!rows || headerIndex === -1) {
        // If we can't find the headers, verify we throw or handle appropriately.
        // For now, consistent error.
        throw new Error('Cabeçalho não identificado no arquivo da Wellhub.');
    }

    const indexes = resolveIndexes(rows[headerIndex] as (string | number | null)[]);
    const records = parseRows(rows, headerIndex, indexes);
    const totals = records.reduce(
        (acc, record) => {
            acc.feeCount += 1;
            acc.amount += record.amount;
            return acc;
        },
        { dependents: 0, feeCount: 0, amount: 0 },
    );

    return { records, totals };
}
